import type { Message } from '../InlineChat';
import type { PortalOpenDocumentMeta, PortalSelectedDocumentPayload } from './portalDocuments';
import { parseReasoningTrace, type ReasoningTrace } from './reasoningTrace';
import { portalAuthzHeaders } from '../../constants';
import { hasUsableAkteExtract } from './portalAkteLazyExtract';
import {
  BACKGROUND_DOCUMENT_ANALYSIS_PROMPT,
  BACKGROUND_DOCUMENT_ANALYSIS_SYNTHESIS_PROMPT,
} from './portalBackgroundAnalysisChat';
import {
  PORTAL_ARKO_LLM_WINDOW_CHARS,
  buildSectionWindowContext,
  buildSynthesisContext,
  composeBackgroundAnalysisWorkerText,
  coverageFromPlan,
  joinPriorNotes,
  planAnalysisWindows,
  preferFullExtractForAnalysis,
  trimAnalysisNotes,
  type AnalysisCoverage,
  type AnalysisProgress,
  type AnalysisStopReason,
} from './portalBackgroundAnalysisChunks';

/** Phase 2a: Queue + Recovery · Phase 2b: bis zu 2 parallel. */
export const MAX_PARALLEL_BACKGROUND_JOBS = 2;

/** v3: Mini-Extract-Snapshots (Convert-Titel) nicht wiederaufnehmen. */
const BACKGROUND_JOBS_STORAGE_PREFIX = 'arkorbit_portal_arko_background_jobs_v3__';
/** Phase-1-Singleton (Migration). */
const LEGACY_BACKGROUND_JOB_STORAGE_PREFIX = 'arkorbit_portal_arko_background_job_v1__';

export interface BackgroundJobSnapshot {
  documentId: string;
  documentName: string;
  request_id: string;
  startedAt: number;
  threadId: string;
  capturedHistory: Array<Pick<Message, 'role' | 'text'>>;
  capturedDocumentContext: string;
  capturedOpenDocuments?: PortalOpenDocumentMeta[];
  capturedSelectedDocuments?: PortalSelectedDocumentPayload[];
  capturedFingerprint?: string | null;
  capturedExtraBody?: Record<string, unknown>;
  capturedExtractPages?: number;
  capturedExtractRawChars?: number;
  capturedExtractTruncated?: boolean;
  capturedExtractSource?: string;
  mode?: string;
}

interface BackgroundJobQueueFile {
  version: 2;
  jobs: BackgroundJobSnapshot[];
}

export interface BackgroundJobCompletionNotice {
  id: string;
  threadId: string;
  documentName: string;
  success: boolean;
  summary: string;
}

export function backgroundJobsStorageKey(customerId: string): string {
  return `${BACKGROUND_JOBS_STORAGE_PREFIX}${customerId}`;
}

function legacyBackgroundJobStorageKey(customerId: string): string {
  return `${LEGACY_BACKGROUND_JOB_STORAGE_PREFIX}${customerId}`;
}

function isValidSnapshot(value: unknown): value is BackgroundJobSnapshot {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const o = value as Partial<BackgroundJobSnapshot>;
  return (
    typeof o.documentId === 'string'
    && typeof o.documentName === 'string'
    && typeof o.request_id === 'string'
    && typeof o.startedAt === 'number'
    && typeof o.threadId === 'string'
    && typeof o.capturedDocumentContext === 'string'
    && Array.isArray(o.capturedHistory)
  );
}

function migrateLegacySingleJob(customerId: string): BackgroundJobSnapshot[] {
  try {
    const raw = sessionStorage.getItem(legacyBackgroundJobStorageKey(customerId));
    if (!raw) return [];
    const value = JSON.parse(raw) as Partial<BackgroundJobSnapshot>;
    sessionStorage.removeItem(legacyBackgroundJobStorageKey(customerId));
    if (!isValidSnapshot(value)) return [];
    return [value];
  } catch {
    return [];
  }
}

export function loadBackgroundJobQueue(customerId: string): BackgroundJobSnapshot[] {
  try {
    const raw = sessionStorage.getItem(backgroundJobsStorageKey(customerId));
    if (!raw) return migrateLegacySingleJob(customerId);
    const parsed = JSON.parse(raw) as Partial<BackgroundJobQueueFile> | BackgroundJobSnapshot[];
    if (Array.isArray(parsed)) {
      return parsed.filter(isValidSnapshot);
    }
    if (parsed?.version === 2 && Array.isArray(parsed.jobs)) {
      return parsed.jobs.filter(isValidSnapshot);
    }
    sessionStorage.removeItem(backgroundJobsStorageKey(customerId));
    return migrateLegacySingleJob(customerId);
  } catch {
    return migrateLegacySingleJob(customerId);
  }
}

export function saveBackgroundJobQueue(customerId: string, jobs: BackgroundJobSnapshot[]): void {
  try {
    const key = backgroundJobsStorageKey(customerId);
    if (jobs.length === 0) {
      sessionStorage.removeItem(key);
      return;
    }
    const payload: BackgroundJobQueueFile = { version: 2, jobs };
    sessionStorage.setItem(key, JSON.stringify(payload));
  } catch {
    /* privater Modus — React-State bleibt maßgeblich */
  }
}

export function clearBackgroundJobStorage(customerId: string): void {
  try { sessionStorage.removeItem(backgroundJobsStorageKey(customerId)); } catch { /* noop */ }
  try { sessionStorage.removeItem(legacyBackgroundJobStorageKey(customerId)); } catch { /* noop */ }
}

export function newBackgroundRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `bg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function shortDocumentLabel(name: string, max = 26): string {
  const trimmed = name.trim() || 'Dokument';
  return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed;
}

export {
  buildBackgroundAnalysisChatContent,
  splitBackgroundAnalysisReply,
  type BackgroundAnalysisChatContent,
} from './portalBackgroundAnalysisChat';

export function backgroundReasoningTrace(
  requestId: string,
  parsed: ReasoningTrace | null,
  error: string | null = null,
): ReasoningTrace {
  if (parsed) {
    return {
      ...parsed,
      request_id: parsed.request_id ?? requestId,
      pipeline: [
        { step: 'background_document_analysis', ok: error == null },
        ...(parsed.pipeline ?? []),
      ],
      error: parsed.error ?? error,
    };
  }
  return {
    intent: 'zusammenfassen',
    path: 'background_document_analysis',
    not_triggered: [],
    request_id: requestId,
    pipeline: [{ step: 'background_document_analysis', ok: error == null }],
    warnings: [],
    error,
  };
}

export function threadHasBackgroundJobResult(
  messages: Message[] | undefined,
  requestId: string,
): boolean {
  if (!messages?.length) return false;
  return messages.some((msg) => msg.reasoningTrace?.request_id === requestId);
}

export interface RunBackgroundJobParams {
  job: BackgroundJobSnapshot;
  webhookUrl: string;
  timeoutMs: number;
  signal: AbortSignal;
  /** Snapshot at call time — prefer {@link isCancelled} for in-flight abort. */
  cancelled?: boolean;
  isCancelled?: () => boolean;
  onProgress?: (progress: AnalysisProgress) => void;
}

export type BackgroundJobRunOutcome =
  | { kind: 'success'; reply: string; workerText: string; coverage: AnalysisCoverage; trace: ReasoningTrace }
  | { kind: 'error'; trace: ReasoningTrace; timedOut: boolean; cancelled: boolean; coverage?: AnalysisCoverage; workerText?: string };

export function extraBodyForFreshAnalysisWindow(
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  if (!extra) return {};
  const next = { ...extra };
  delete next.memContext;
  delete next.history;
  delete next.judicatureMode;
  delete next.judicature_mode;
  delete next.webSearchMode;
  delete next.web_search_mode;
  delete next.lexCarry;
  delete next.preferStream;
  delete next.stream;
  delete next.intent;
  return next;
}

export function backgroundAnalysisChatInput(
  documentName: string,
  selectedCount: number,
  selectedTitles?: string[],
  phase: 'section' | 'synthesis' = 'section',
): string {
  const fromTitles = (selectedTitles ?? []).map((t) => t.trim()).filter(Boolean);
  let list = fromTitles.join(', ');
  if (!list && selectedCount >= 2) {
    list = String(documentName || '').split(/\s*,\s*/).map((t) => t.trim()).filter(Boolean).join(', ');
  }
  if (!list) list = documentName.trim() || 'Dokument';
  const prompt = phase === 'synthesis'
    ? BACKGROUND_DOCUMENT_ANALYSIS_SYNTHESIS_PROMPT
    : BACKGROUND_DOCUMENT_ANALYSIS_PROMPT;
  return `${prompt} Dokumente: ${list}.`;
}

export function analysisSourceContextFromJob(
  job: BackgroundJobSnapshot,
): string {
  return preferFullExtractForAnalysis(job.capturedDocumentContext, null);
}

function coverageExtraFromJob(job: BackgroundJobSnapshot) {
  return {
    extractPages: job.capturedExtractPages,
    extractRawChars: job.capturedExtractRawChars,
    extractTruncated: job.capturedExtractTruncated,
    extractSource: job.capturedExtractSource,
  };
}

type BackgroundAnalysisStepArgs = {
  job: BackgroundJobSnapshot;
  documentContext: string;
  phase: 'section' | 'synthesis';
  chunkIndex: number;
  chunkTotal: number;
  documentTitles: string[];
};

export function buildBackgroundAnalysisRequestBody(
  job: BackgroundJobSnapshot,
  selectedDocs: PortalSelectedDocumentPayload[],
  step?: Partial<BackgroundAnalysisStepArgs>,
): Record<string, unknown> {
  const isMultiSelect = selectedDocs.length >= 1;
  const selectedTitles = (step?.documentTitles?.length
    ? step.documentTitles
    : selectedDocs.map((d) => String(d.title || '').trim()).filter(Boolean));
  const phase = step?.phase ?? 'section';
  const chunkIndex = step?.chunkIndex ?? 1;
  const chunkTotal = step?.chunkTotal ?? 1;
  const documentContext = step?.documentContext ?? job.capturedDocumentContext;
  const { documentId, documentName, request_id: requestId, threadId } = job;
  return {
    chatInput: backgroundAnalysisChatInput(
      documentName,
      isMultiSelect ? selectedDocs.length : 1,
      selectedTitles,
      phase,
    ),
    mode: job.mode,
    request_id: requestId,
    ...extraBodyForFreshAnalysisWindow(job.capturedExtraBody),
    threadId,
    documentContext,
    activeDocumentId: documentId,
    ...(job.capturedOpenDocuments?.length ? { openDocuments: job.capturedOpenDocuments } : {}),
    ...(job.capturedFingerprint ? { documentExtractFingerprint: job.capturedFingerprint } : {}),
    history: [],
    intent: 'zusammenfassen',
    background: true,
    background_job: true,
    backgroundJob: {
      kind: 'document_analysis',
      documentId,
      startedAt: job.startedAt,
      phase,
      chunkIndex,
      chunkTotal,
      windowChars: PORTAL_ARKO_LLM_WINDOW_CHARS,
      ...(isMultiSelect ? { selectedDocumentIds: selectedDocs.map((d) => d.documentId) } : {}),
    },
  };
}

function extractWebhookReply(payload: unknown, fallback: unknown): string {
  const p = payload as Record<string, unknown> | null | undefined;
  const reply =
    p?.output ?? p?.text ?? p?.message ??
    (typeof p?.message === 'object' && p?.message !== null
      ? (p.message as { content?: unknown }).content
      : undefined) ??
    p?.content ??
    (typeof payload === 'string' ? payload : null);
  if (typeof reply === 'string' && reply.trim()) return reply;
  return typeof fallback === 'string' ? fallback : JSON.stringify(fallback);
}

async function postBackgroundAnalysisStep(args: {
  webhookUrl: string;
  requestId: string;
  body: Record<string, unknown>;
  timeoutMs: number;
  parentSignal: AbortSignal;
}): Promise<{ ok: true; reply: string; trace: ReasoningTrace | null } | { ok: false; aborted: boolean }> {
  const controller = new AbortController();
  const onParentAbort = () => controller.abort();
  if (args.parentSignal.aborted) controller.abort();
  else args.parentSignal.addEventListener('abort', onParentAbort, { once: true });
  const timer = setTimeout(() => controller.abort(), args.timeoutMs);
  try {
    const response = await fetch(args.webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Request-Id': args.requestId,
        ...portalAuthzHeaders(args.webhookUrl),
      },
      body: JSON.stringify(args.body),
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const data = await response.json();
    const raw = data?.data ?? data;
    const item = Array.isArray(raw) ? raw[0] : raw;
    const payload = item?.json ?? item ?? data;
    return {
      ok: true,
      reply: extractWebhookReply(payload, data),
      trace: parseReasoningTrace(payload?.reasoning_trace),
    };
  } catch (error) {
    const aborted = (error as { name?: string })?.name === 'AbortError';
    if (aborted) return { ok: false, aborted: true };
    throw error;
  } finally {
    clearTimeout(timer);
    args.parentSignal.removeEventListener('abort', onParentAbort);
  }
}

export async function runBackgroundDocumentAnalysis(
  params: RunBackgroundJobParams & { cancelled?: boolean },
): Promise<BackgroundJobRunOutcome> {
  const { job, webhookUrl, timeoutMs, signal, cancelled = false, isCancelled, onProgress } = params;
  const userCancelled = () => (isCancelled?.() ?? cancelled);
  const requestId = job.request_id;
  const selectedDocs = job.capturedSelectedDocuments?.filter((d) => hasUsableAkteExtract(d.documentContext)) ?? [];
  const sourceContext = analysisSourceContextFromJob(job);
  const plan = planAnalysisWindows(sourceContext);
  if (plan.windows.length === 0) {
    return {
      kind: 'error',
      timedOut: false,
      cancelled: false,
      trace: backgroundReasoningTrace(requestId, null, 'background_request_failed'),
    };
  }
  const titles = selectedDocs.map((d) => String(d.title || '').trim()).filter(Boolean);
  const sectionNotes: string[] = [];
  let lastTrace: ReasoningTrace | null = null;

  const fail = (
    stopReason: AnalysisStopReason,
    timedOut: boolean,
    cancelledNow: boolean,
  ): BackgroundJobRunOutcome => {
    const coverage = coverageFromPlan(plan, sectionNotes.length, stopReason, coverageExtraFromJob(job));
    const workerText = sectionNotes.length
      ? composeBackgroundAnalysisWorkerText({
        synthesisReply: sectionNotes[sectionNotes.length - 1] || '',
        sectionNotes,
        coverage,
      })
      : undefined;
    return {
      kind: 'error',
      timedOut,
      cancelled: cancelledNow,
      coverage,
      workerText,
      trace: backgroundReasoningTrace(
        requestId,
        lastTrace,
        cancelledNow ? 'background_cancelled' : timedOut ? 'background_timeout' : 'background_request_failed',
      ),
    };
  };

  try {
    for (let i = 0; i < plan.windows.length; i += 1) {
      if (userCancelled() || signal.aborted) {
        return fail('cancelled', false, true);
      }
      const chunkIndex = i + 1;
      const priorNotes = joinPriorNotes(sectionNotes);
      const charsRead = plan.windows.slice(0, chunkIndex).reduce((n, w) => n + w.length, 0);
      onProgress?.({
        chunkIndex,
        chunkTotal: plan.chunksPlanned,
        phase: 'section',
        notes: priorNotes,
        charsRead,
        charsTotal: plan.extractChars,
      });
      const documentContext = buildSectionWindowContext({
        sourceContext,
        windowText: plan.windows[i],
        chunkIndex,
        chunkTotal: plan.chunksPlanned,
        remainderChars: plan.remainderChars,
        priorNotes,
      });
      const posted = await postBackgroundAnalysisStep({
        webhookUrl,
        requestId,
        timeoutMs,
        parentSignal: signal,
        body: buildBackgroundAnalysisRequestBody(job, selectedDocs, {
          documentContext,
          phase: 'section',
          chunkIndex,
          chunkTotal: plan.chunksPlanned,
          documentTitles: titles,
        }),
      });
      if (!posted.ok) {
        const cancelledNow = userCancelled();
        return fail(cancelledNow ? 'cancelled' : 'timeout', !cancelledNow, cancelledNow);
      }
      lastTrace = posted.trace;
      sectionNotes.push(trimAnalysisNotes(posted.reply));
    }

    const reachedCap = plan.chunksPlanned > plan.chunksCapped;
    let stopReason: AnalysisStopReason = reachedCap ? 'max_chunks' : 'complete';
    let synthesisReply = sectionNotes[sectionNotes.length - 1] || '';
    const needsSynthesis = sectionNotes.length > 1 || reachedCap || plan.remainderChars > 0;

    if (needsSynthesis && sectionNotes.length > 0) {
      if (userCancelled() || signal.aborted) {
        return fail('cancelled', false, true);
      }
      const coveragePreview = coverageFromPlan(plan, sectionNotes.length, stopReason, coverageExtraFromJob(job));
      onProgress?.({
        chunkIndex: sectionNotes.length,
        chunkTotal: plan.chunksPlanned,
        phase: 'synthesis',
        notes: joinPriorNotes(sectionNotes),
        charsRead: plan.windows.reduce((n, w) => n + w.length, 0),
        charsTotal: plan.extractChars,
      });
      const posted = await postBackgroundAnalysisStep({
        webhookUrl,
        requestId,
        timeoutMs,
        parentSignal: signal,
        body: buildBackgroundAnalysisRequestBody(job, selectedDocs, {
          documentContext: buildSynthesisContext({
            sourceContext,
            notes: sectionNotes,
            coverage: coveragePreview,
          }),
          phase: 'synthesis',
          chunkIndex: sectionNotes.length,
          chunkTotal: plan.chunksPlanned,
          documentTitles: titles,
        }),
      });
      if (!posted.ok) {
        const cancelledNow = userCancelled();
        if (cancelledNow) return fail('cancelled', false, true);
        stopReason = 'timeout';
        // Timeout nach gelesenen Abschnitten: ehrliche Teilsynthese aus Notizen.
      } else {
        lastTrace = posted.trace;
        synthesisReply = posted.reply.trim() || synthesisReply;
      }
    }

    const coverage = coverageFromPlan(plan, sectionNotes.length, stopReason, coverageExtraFromJob(job));
    return {
      kind: 'success',
      reply: synthesisReply,
      workerText: composeBackgroundAnalysisWorkerText({
        synthesisReply,
        sectionNotes,
        coverage,
      }),
      coverage,
      trace: backgroundReasoningTrace(requestId, lastTrace),
    };
  } catch (error) {
    const aborted = (error as { name?: string })?.name === 'AbortError';
    const cancelledNow = userCancelled();
    const timedOut = aborted && !cancelledNow;
    if (sectionNotes.length > 0) {
      const coverage = coverageFromPlan(
        plan,
        sectionNotes.length,
        timedOut ? 'timeout' : cancelledNow ? 'cancelled' : 'error',
        coverageExtraFromJob(job),
      );
      return {
        kind: 'success',
        reply: sectionNotes.join(' '),
        workerText: composeBackgroundAnalysisWorkerText({
          synthesisReply: sectionNotes.join('\n'),
          sectionNotes,
          coverage,
        }),
        coverage,
        trace: backgroundReasoningTrace(
          requestId,
          lastTrace,
          timedOut ? 'background_timeout' : cancelledNow ? 'background_cancelled' : 'background_request_failed',
        ),
      };
    }
    return {
      kind: 'error',
      timedOut,
      cancelled: cancelledNow || (aborted && !timedOut),
      trace: backgroundReasoningTrace(
        requestId,
        lastTrace,
        cancelledNow ? 'background_cancelled' : timedOut ? 'background_timeout' : 'background_request_failed',
      ),
    };
  }
}

export function buildBackgroundJobSnapshot(args: {
  documentId: string;
  documentName: string;
  threadId: string;
  history?: Message[];
  documentContext: string;
  openDocumentsMeta?: PortalOpenDocumentMeta[];
  selectedDocuments?: PortalSelectedDocumentPayload[];
  documentExtractFingerprint?: string | null;
  extraBody?: Record<string, unknown>;
  mode?: string;
  extractPages?: number;
  extractRawChars?: number;
  extractTruncated?: boolean;
  extractSource?: string;
}): BackgroundJobSnapshot {
  return {
    documentId: args.documentId,
    documentName: args.documentName,
    request_id: newBackgroundRequestId(),
    startedAt: Date.now(),
    threadId: args.threadId,
    capturedHistory: [],
    capturedDocumentContext: args.documentContext,
    capturedOpenDocuments: args.openDocumentsMeta,
    capturedSelectedDocuments: args.selectedDocuments,
    capturedFingerprint: args.documentExtractFingerprint,
    capturedExtraBody: extraBodyForFreshAnalysisWindow(args.extraBody),
    capturedExtractPages: args.extractPages,
    capturedExtractRawChars: args.extractRawChars,
    capturedExtractTruncated: args.extractTruncated,
    capturedExtractSource: args.extractSource,
    mode: args.mode,
  };
}
